import { Card } from "../../../../../components/Card";
import React from "react";
import { TooltipView } from "../../../../../components/Tooltip";
import { useAnalytics } from "../../../../../integrations/posthog/useAnalytics";
import { PRAXIS_PLAY_EVENTS } from "../../../../../integrations/posthog/events";
import { useApp } from "../../../../../services/app-context";
import { getPerformanceColor } from "../../../../../services/utils";
import { getAverageMetricScore, getReportInsights } from "../../../../../services/evalCategoryUtils";

const filteredMetrics = (metrics: any, metric_exclusions: any) => {
    return Object.keys(metrics).filter((metric) => metric_exclusions?.[metric]?.show !== false);
}

const shouldShowColor = (metric: string, metric_exclusions: any) => {
    return metric_exclusions[metric]?.show_colors !== false;
}

const shouldShowPercentage = (metric: string, metric_exclusions: any) => {
    return metric_exclusions[metric]?.show_percentages !== false;
}


function parseFeedbackWithTitles(text) {
    const lines = text.split('\n');
    const result = {};
    let currentTitle: string | null = null;

    for (const line of lines) {
        const trimmed = line.trim();

        // Detect section titles starting with '#'
        if (trimmed.startsWith('#')) {
            currentTitle = trimmed.replace(/^#+/, '').trim(); // Remove one or more '#' characters
            if (currentTitle) {
                result[currentTitle] = [] as string[];
            }
        }
        // Detect bullet points starting with '-'
        else if (trimmed.startsWith('-') && currentTitle) {
            result[currentTitle].push(trimmed.slice(1).trim()) as string[];
        }
    }

    return result;
}

function InsightsView({ activeTab, setActiveTab, detailedReport }: { activeTab: string, setActiveTab: (tab: string) => void, detailedReport: any }) {
    const { captureEvent } = useAnalytics();
    const { tenantProfile } = useApp();
    const metric_ranges = tenantProfile?.metric_ranges || [];
    const info_badges = tenantProfile?.info_badges || {};
    const aggregatedReportFeedback = detailedReport?.aggregatedReportFeedback?.[activeTab] || "";
    const structuredData = parseFeedbackWithTitles(aggregatedReportFeedback);

    const metric_exclusions = tenantProfile?.metric_exclusions || {};
    const detailedReportMetricExclusions = metric_exclusions?.detailed_reports || {};

    const feedbacks = React.useMemo(() => {
        return detailedReport?.report?.map((item) => {
            const mergedMetrics = { ...item?.report?.constant_metrics, ...item?.report?.variable_metrics };
            return mergedMetrics?.[activeTab]?.markdown || "";
        }) || []
    }, [detailedReport?.report, activeTab]);

    const onMetricClick = (metric: string, metricIndex: number) => {
        setActiveTab(metric)
        captureEvent(PRAXIS_PLAY_EVENTS.DETAILED_REPORT_METRIC_SELECTED, {
            metric_type: metric,
            metric_index: metricIndex,
            session_id: detailedReport?.session?.sessionID,
        })
    }

    const metricsColor: { progressBg?: string, text?: string } = React.useMemo(() => {
        if (shouldShowColor(activeTab, detailedReportMetricExclusions)) {
            const percentage = getAverageMetricScore(detailedReport?.aggregatedReportScores, activeTab);
            return getPerformanceColor(percentage, metric_ranges || []);
        }
        return {};
    }, [activeTab, detailedReportMetricExclusions]);

    const metrics = getReportInsights(tenantProfile?.dashboard_insights?.metrics);

    // Each insight highlights what impacted your Praxis Score and where you can improve.

    return <Card>
        <div className="flex">
            <div className="flex flex-col gap-2">
                <div className="mb-3 flex items-center gap-2">
                    <span className="text-sm font-semibold">Core Competency Insights</span>
                    {!!info_badges?.praxispro_insights && (<TooltipView content={info_badges?.praxispro_insights} id="insights" iconClassName="!w-5 !h-5" />
                    )}
                </div>
                <div className="flex flex-col gap-2 max-h-[calc(100vh-300px)] pb-4 overflow-y-auto">
                    {filteredMetrics(metrics, metric_exclusions?.detailed_reports || {}).map((metric, index) => (
                        <div className="flex items-center" key={metric}>
                            <div onClick={() => {
                                onMetricClick(metric, index)
                            }} className={`cursor-pointer p-3.5 w-[300px]  flex flex-col justify-center items-center gap-[5px] bg-zinc-100 ${activeTab === metric ? `rounded-l-lg ` : "rounded-lg"}`} style={{ backgroundColor: activeTab === metric  ? metricsColor?.progressBg : "" }}>
                                <div className="self-stretch flex justify-between items-start">
                                    <div className="justify-start text-stone-900 text-sm font-normal leading-snug">{metrics[metric] || metric}</div>
                                    {shouldShowPercentage(metric, metric_exclusions?.detailed_reports || {}) && <div
                                        style={{ color: shouldShowColor(metric, metric_exclusions?.detailed_reports || {}) ? getPerformanceColor(getAverageMetricScore(detailedReport?.aggregatedReportScores, metric), metric_ranges || [])?.text || "#000000" : "#000000" }}
                                        className={`justify-start text-sm font-bold leading-normal`}>{getAverageMetricScore(detailedReport?.aggregatedReportScores, metric).toFixed(0)}%</div>}
                                </div>
                            </div>
                            <div className={`w-[30px] h-full`} style={{ backgroundColor: activeTab === metric  ? metricsColor?.progressBg : "transparent" }}>

                            </div>
                        </div>
                    ))}

                </div>
            </div>
            {activeTab && <div className={`flex flex-col gap-2 flex-1 pt-[12px] rounded-[10px] bg-zinc-100`} style={{ backgroundColor: metricsColor?.progressBg }}>
                <div className={`flex justify-between items-center px-[18px]`} >
                    <h3 className="text-lg font-bold text-[#464646]">{metrics[activeTab] || activeTab}</h3>
                    {shouldShowPercentage(activeTab, metric_exclusions?.detailed_reports || {}) && <span className="text-lg font-bold" style={{ color: metricsColor?.text }}>{getAverageMetricScore(detailedReport?.aggregatedReportScores, activeTab).toFixed(0)}%</span>}
                </div>
                <div className="flex flex-col gap-2 px-[18px] overflow-y-auto max-h-[calc(100vh-320px)]">
                    <div className="pb-4">
                        {aggregatedReportFeedback && <>
                            {/* <h2 className="text-[15px] font-medium mb-3">Action Items</h2> */}
                            <div>
                                {/** nested list */}
                                {Object.keys(structuredData).map((item, index) => (
                                    <div key={index}>
                                        <div className="!text-sm font-medium my-3">{item}</div>
                                        <ul className="list-disc space-y-2 mx-2.5">
                                            {structuredData[item].map((item, index) => (
                                                <li key={index} className="text-xs">
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </>}
                    </div>
                </div>
            </div>}
        </div>
    </Card>;
}

export default InsightsView;
